import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { useI18n } from '../../contexts/I18nContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowRight, 
  Shield, 
  TrendingUp, 
  Building2, 
  Users,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';

const HERO_BG = "https://images.unsplash.com/photo-1579281959510-26b99ae23ae3?q=80&w=2574&auto=format&fit=crop";
const LOGO_URL = "/logo-white.png";

const HomePage = () => {
  const { t, formatCurrency } = useI18n();
  const [featuredOpportunities, setFeaturedOpportunities] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  // Normalize opportunity data from different API formats
  const normalizeOpp = (opp) => {
    const loc = opp.location;
    const locationStr = typeof loc === 'string' ? loc 
      : (loc && typeof loc === 'object') ? [loc.city, loc.state].filter(Boolean).join(', ')
      : opp.location_city ? [opp.location_city, opp.location_state].filter(Boolean).join(', ')
      : '';
    
    // Get the best available image for each context
    const galleryFirst = Array.isArray(opp.gallery_images) && opp.gallery_images.length > 0 ? opp.gallery_images[0] : null;
    const legacyImage = opp.image || (opp.images && opp.images[0]) || (opp.media_gallery && opp.media_gallery[0]) || '';
    
    return {
      ...opp,
      title: opp.title || opp.title_es || opp.title_en || '',
      type: opp.type || opp.project_type || opp.investment_structure || '',
      location: locationStr,
      // Processed variants (preferred) → legacy fallback
      image_card: opp.image_card_url || (galleryFirst && galleryFirst.image_card_url) || legacyImage,
      image_cover: opp.image_cover_url || (galleryFirst && galleryFirst.image_cover_url) || legacyImage,
      image_hero: opp.image_hero_url || (galleryFirst && galleryFirst.image_hero_url) || legacyImage,
      image_thumbnail: opp.image_thumbnail_url || (galleryFirst && galleryFirst.image_thumbnail_url) || legacyImage,
      image: opp.image_cover_url || (galleryFirst && galleryFirst.image_cover_url) || legacyImage,
      // Developer name with fallback chain
      developer: opp.developer_name || opp.operator_name || opp.sponsor_name || 'Desarrollador por confirmar',
      minimum_investment: opp.minimum_investment || (opp.financial && opp.financial.minimum_investment) || opp.capital_target || 0,
      projected_irr: opp.projected_irr || opp.projected_irr_base || (opp.financial && opp.financial.projected_irr) || null,
      status: opp.status || 'published',
    };
  };


  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await api.get('/opportunities/featured');
        setFeaturedOpportunities((response.data.opportunities || []).map(normalizeOpp));
      } catch (error) {
        console.error('Error fetching featured:', error);
        // Fallback a obtener todas las oportunidades
        try {
          const fallback = await api.get('/opportunities?limit=6');
          setFeaturedOpportunities((fallback.data.opportunities || []).map(normalizeOpp));
        } catch (e) {
          console.error('Fallback error:', e);
        }
      } finally {
        setLoadingFeatured(false);
      }
    };
    fetchFeatured();
  }, []);

  const features = [
    {
      icon: Shield,
      title: 'Disciplina Institucional',
      description: 'Procesos rigurosos de due diligence y estructuración legal para cada oportunidad.'
    },
    {
      icon: TrendingUp,
      title: 'Rendimientos Estructurados',
      description: 'Estructuras de inversión diseñadas para optimizar rendimientos ajustados al riesgo.'
    },
    {
      icon: Building2,
      title: 'Activos Premium',
      description: 'Acceso a proyectos inmobiliarios de alta calidad previamente vetados.'
    },
    {
      icon: Users,
      title: 'Gobernanza de Grado Corporativo',
      description: 'Comunicación constante y procesos transparentes en vehículos jurídicos y fiscales adecuados. Desde convenios de mediación hasta fideicomisos, con información clara, reportes periódicos y enlace directo con el equipo.'
    },
  ];

  const steps = [
    { number: '01', title: 'Registro', description: 'Cree su cuenta y complete su perfil de inversionista.' },
    { number: '02', title: 'Verificación', description: 'Complete el proceso de verificación y suitability.' },
    { number: '03', title: 'Exploración', description: 'Revise las oportunidades disponibles y sus documentos.' },
    { number: '04', title: 'Inversión', description: 'Comprometa capital y firme los documentos necesarios.' },
  ];

  const sampleOpportunities = [
    {
      title: 'Torre Reforma Elite',
      type: 'Desarrollo Mixto',
      location: 'Ciudad de México',
      minInvestment: 500000,
      targetIRR: '18-22%',
      status: 'open',
      image: 'https://images.unsplash.com/photo-1579283987703-5ae978f95ace?q=80&w=2574&auto=format&fit=crop'
    },
    {
      title: 'Industrial Park Querétaro',
      type: 'Industrial',
      location: 'Querétaro',
      minInvestment: 250000,
      targetIRR: '14-18%',
      status: 'open',
      image: 'https://images.pexels.com/photos/1707823/pexels-photo-1707823.jpeg'
    },
    {
      title: 'Residencial Bosques',
      type: 'Residencial',
      location: 'Monterrey',
      minInvestment: 350000,
      targetIRR: '16-20%',
      status: 'soft_circle',
      image: 'https://images.unsplash.com/photo-1671722294182-ed01cbe66bd1?q=80&w=2670&auto=format&fit=crop'
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/70 to-background" />
        </div>
        
        <div className="container-app relative z-10 pt-32 pb-24">
          <div className="max-w-4xl">
            {/* Logo Grande */}
            <div className="mb-10">
              <img 
                src="/logo-white.png" 
                alt="Silver Brick Development" 
                className="h-[400px] md:h-[500px] lg:h-[600px] w-auto object-contain"
              />
            </div>
            
            <Badge variant="outline" className="mb-8 text-xs uppercase tracking-widest">
              Inversión Inmobiliaria Blindada
            </Badge>
            
            <h1 className="heading-display mb-8" data-testid="hero-title">
              Acceso a Oportunidades de Inversión<br />
              <span className="text-muted-foreground">de Grado Institucional</span>
            </h1>
            
            <p className="body-lg max-w-2xl mb-12">
              {t('hero.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/opportunities">
                <Button className="btn-primary gap-2" data-testid="explore-btn">
                  {t('hero.cta.explore')}
                  <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
              <Link to="/submit-property">
                <Button variant="outline" className="btn-outline" data-testid="submit-property-btn">
                  {t('hero.cta.submit')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-muted-foreground/50 to-transparent" />
        </div>
      </section>

      {/* Trust Section */}
      <section className="section-spacing border-t border-border">
        <div className="container-app">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <div className="text-center">
              <p className="heading-2 mb-2">$50M+ USD</p>
              <p className="caption">Capital Administrado</p>
            </div>
            <div className="text-center">
              <p className="heading-2 mb-2">12+</p>
              <p className="caption">Proyectos Completados</p>
            </div>
            <div className="text-center">
              <p className="heading-2 mb-2">18%</p>
              <p className="caption">TIR Promedio</p>
            </div>
            <div className="text-center">
              <p className="heading-2 mb-2">100+</p>
              <p className="caption">Inversionistas</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-spacing bg-card">
        <div className="container-app">
          <div className="text-center mb-16">
            <p className="caption mb-4">Por Qué Silver Brick</p>
            <h2 className="heading-1">
              Disciplina e Integridad
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="card-premium">
                  <Icon className="h-8 w-8 mb-6 text-primary" strokeWidth={1} />
                  <h3 className="text-lg font-medium mb-3">{feature.title}</h3>
                  <p className="body-text text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="caption mb-4">Proceso</p>
              <h2 className="heading-1 mb-8">
                Cómo Funciona
              </h2>
              <div className="space-y-8">
                {steps.map((step, index) => (
                  <div key={index} className="flex gap-6">
                    <span className="text-4xl font-light text-muted-foreground/30">
                      {step.number}
                    </span>
                    <div>
                      <h3 className="text-lg font-medium mb-2">{step.title}</h3>
                      <p className="body-text text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-12">
                <Link to="/how-it-works">
                  <Button variant="outline" className="gap-2">
                    Más Información
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square bg-muted rounded-sm overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1670383050616-682df7d57b22?q=80&w=2670&auto=format&fit=crop"
                  alt="Team meeting"
                  className="w-full h-full object-cover opacity-80"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-2/3 aspect-video bg-card border border-border rounded-sm p-6">
                <p className="caption mb-2">Compromiso Total</p>
                <p className="text-sm text-muted-foreground">
                  Cada proyecto es revisado exhaustivamente por nuestro equipo legal, financiero y técnico.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Opportunities */}
      <section className="section-spacing bg-card">
        <div className="container-app">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
            <div>
              <p className="caption mb-4">Oportunidades</p>
              <h2 className="heading-1">
                Proyectos Destacados
              </h2>
            </div>
            <Link to="/opportunities">
              <Button variant="outline" className="gap-2">
                Ver Todas
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredOpportunities.map((opp, index) => (
              <Link 
                key={index} 
                to={`/oportunidades/${opp.slug}`}
                className="group"
              >
                <div className="card-premium p-0 overflow-hidden">
                  <div className="aspect-[4/3] relative overflow-hidden">
                    {opp.image_card ? (
                      <img 
                        src={opp.image_card}
                        alt={opp.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        data-testid={`project-card-image-${index}`}
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                      />
                    ) : null}
                    <div className={`w-full h-full bg-muted items-center justify-center ${opp.image_card ? 'hidden' : 'flex'}`}>
                      <span className="text-muted-foreground text-sm">Sin imagen</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
                    <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                      <span className="text-xs font-medium text-white/90 bg-black/40 backdrop-blur-sm px-3 py-1 rounded" data-testid={`developer-name-${index}`}>
                        {opp.developer}
                      </span>
                      <Badge 
                        className="uppercase text-xs"
                        variant={opp.status === 'open' ? 'default' : 'secondary'}
                      >
                        {opp.status === 'open' ? 'Abierto' : 'Soft Circle'}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="caption mb-2">{opp.type} • {opp.location}</p>
                    <h3 className="text-xl font-medium mb-4 group-hover:text-primary transition-colors">
                      {opp.title}
                    </h3>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Inversión Mínima</p>
                        <p className="font-medium">{formatCurrency(opp.minimum_investment || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground mb-1">TIR Objetivo</p>
                        <p className="font-medium text-primary">{opp.projected_irr ? `${opp.projected_irr}%` : "TBD"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Disclaimer */}
      <section className="py-12 border-t border-border">
        <div className="container-app">
          <div className="flex items-start gap-4 max-w-4xl mx-auto">
            <Shield className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Aviso Importante:</strong> {t('legal.riskWarning')} Las cifras presentadas son ilustrativas y están sujetas a cambios. La información aquí contenida no constituye una oferta de valores donde esté prohibido.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-primary text-primary-foreground">
        <div className="container-app text-center">
          <h2 className="heading-1 mb-6">
            Comience a Invertir Hoy
          </h2>
          <p className="body-lg text-primary-foreground/70 max-w-2xl mx-auto mb-12">
            Únase a nuestra red de inversionistas y acceda a oportunidades inmobiliarias de clase institucional.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                className="bg-background text-foreground hover:bg-background/90 rounded-sm px-8 py-4 h-auto text-sm uppercase tracking-wide font-medium"
                data-testid="register-cta-btn"
              >
                Crear Cuenta
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline" 
                className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-sm px-8 py-4 h-auto text-sm uppercase tracking-wide font-medium"
              >
                Contactar
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
